/**
 * GPS capture — the client half of the geofence.
 *
 * templates/base.html wrote a `tabsway_geo` cookie (lat|lng|ts_ms), refreshed
 * every 60 seconds, which PremisesGuardMiddleware read off the request.
 * Convex functions see no cookies and no headers, so the fix travels as an
 * argument on every authenticated call instead.
 *
 * The freshness rule is unchanged and still enforced SERVER-side
 * (lib/geo.ts::isUsableFix, 5 minutes): a client that stops refreshing
 * cannot keep replaying a stale fix, which is the property that made the
 * cookie's max-age meaningful in the first place.
 *
 * One shared fix for the whole app, so a page with twelve live queries asks
 * the browser for a position once rather than twelve times.
 *
 * ─── WHY THIS MODULE HAS A STATUS, AND WHY IT PERSISTS ────────────
 *
 * With GEOFENCE_ENFORCE on, a non-admin call carrying no fix is refused with
 * `off_premises / no_gps`. The browser takes a second or more to produce its
 * first fix — longer if it has to show the permission prompt — so every
 * screen a user landed on straight after signing in fired its queries with
 * no fix at all and was refused. The refusal redirected to /off-premises,
 * which is a FULL PAGE LOAD, which reset this module's in-memory fix to
 * nothing, so the retry raced exactly the same way and lost. The result was
 * an unbreakable loop for every non-admin — admins are exempt server-side,
 * which is why it only ever showed up "on other devices".
 *
 * Two things fix that, and both live here:
 *
 *   1. A STATUS. "I have no fix yet" and "I asked and cannot get one" are
 *      different answers, and only the second one means off-premises.
 *      Callers wait for the first to resolve (ensureGeoFix) instead of
 *      guessing, and the wait is bounded so a browser that never answers
 *      cannot hang the app.
 *   2. PERSISTENCE. The fix is written to sessionStorage on every reading
 *      and restored at boot while it is still inside the server's freshness
 *      window, so a page load — including the /off-premises redirect itself
 *      — no longer throws away a perfectly good fix.
 */

export interface GeoFix {
  lat: number;
  lng: number;
  /** epoch ms when the browser obtained it */
  ts: number;
}

/**
 * "pending"     — asked the browser, no answer yet. No conclusion can be
 *                 drawn, so nothing may treat this as off-premises.
 * "ready"       — a usable fix is available.
 * "unavailable" — the browser cannot or will not give us one: denied,
 *                 unsupported, insecure origin, or it never answered.
 */
export type GeoStatus = "pending" | "ready" | "unavailable";

let current: GeoFix | null = null; // the last PUBLISHED fix — what calls carry
let latest: GeoFix | null = null; // newest reading, published or not
let status: GeoStatus = "pending";
let watching = false;
let lastError: GeolocationPositionError | null = null;

/**
 * The browser's own error bar on the last reading, in metres.
 *
 * Kept OUT of GeoFix on purpose: that object is sent as a Convex argument
 * and its validator is exact, so an extra field would fail validation on
 * every authenticated call. It is here because it is usually the answer when
 * a branch's coordinates are right and staff are still refused — a handset
 * indoors is routinely 30-50 m out, and can be several hundred on wifi
 * positioning alone, which no radius under that will ever contain.
 */
let lastAccuracy: number | null = null;

type Listener = (fix: GeoFix | null) => void;
type StatusListener = (status: GeoStatus) => void;
const listeners = new Set<Listener>();
const statusListeners = new Set<StatusListener>();

/** Resolved the moment `status` first leaves "pending". */
let settleWaiters: Array<() => void> = [];

/** base.html refreshed every 60s; the server accepts a fix up to 5 min old. */
const REFRESH_MS = 60_000;

/**
 * How long to wait for the browser's FIRST answer before concluding there
 * will not be one. getCurrentPosition's own timeout is 8s and does not start
 * counting until the permission prompt is answered, so this has to be longer
 * than that or a slow "Allow" tap would be read as a refusal.
 */
const FIRST_FIX_DEADLINE_MS = 20_000;

// ── persistence ─────────────────────────────────────────────────────
// Same key and same shape as the cookie base.html wrote, for no reason other
// than that it makes the two versions legible side by side.

const STORE_KEY = "tabsway_geo";

/**
 * Only restore a fix with time left on the server's 5-minute clock, with a
 * margin — a fix that expires between the restore and the first query would
 * be worse than none, because it would be refused as stale rather than as
 * missing.
 */
const RESTORE_MAX_AGE_MS = 240_000;

function persist(fix: GeoFix): void {
  try {
    sessionStorage.setItem(STORE_KEY, `${fix.lat}|${fix.lng}|${fix.ts}`);
  } catch {
    /* private mode: the fix simply does not survive a reload */
  }
}

function restore(): GeoFix | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const [lat, lng, ts] = raw.split("|").map(Number);
    if (![lat, lng, ts].every(Number.isFinite)) return null;
    // The server's two rejections, applied here so we never restore a fix it
    // would refuse: null island, and outside the freshness window.
    if (lat === 0 && lng === 0) return null;
    const age = Date.now() - ts;
    if (age < 0 || age > RESTORE_MAX_AGE_MS) return null;
    return { lat, lng, ts };
  } catch {
    return null;
  }
}

// ── state transitions ───────────────────────────────────────────────

function setStatus(next: GeoStatus): void {
  if (status === next) return;
  const wasPending = status === "pending";
  status = next;
  for (const fn of statusListeners) fn(next);
  if (wasPending) {
    const waiters = settleWaiters;
    settleWaiters = [];
    for (const resolve of waiters) resolve();
  }
}

function publish(fix: GeoFix): void {
  current = fix;
  for (const fn of listeners) fn(fix);
  setStatus("ready");
}

/**
 * ─── WHY PUBLISHING IS RATE-LIMITED ───────────────────────────────
 *
 * The fix travels as an ARGUMENT on every authenticated query, so a new fix
 * object is a new set of arguments, and a new set of arguments is a new
 * subscription: Convex re-runs the query and bills the read.
 *
 * Publishing every 60 seconds therefore re-ran EVERY live query on EVERY
 * open tab once a minute, forever, whether or not anything had changed. On
 * the admin console — one query that reads users, branches, applications,
 * every recipe with its ingredients, settings and audit rows — that is an
 * expensive way to learn that the tablet is still bolted to the same wall.
 *
 * So the browser is still polled every 60 seconds (a fix must be warm, and
 * movement must be noticed promptly), but subscribers are only told when the
 * answer could actually change the server's verdict:
 *
 *   - the position moved more than MIN_MOVE_M, which is what the geofence
 *     measures; or
 *   - the published fix is approaching the server's 5-minute freshness
 *     limit and must be renewed before it goes stale.
 *
 * A stationary tablet re-subscribes roughly every 3.5 minutes instead of
 * every 60 seconds. A moving one still updates within a single poll.
 *
 * PERSISTING is deliberately not rate-limited: writing sessionStorage costs
 * nothing and re-subscribes nothing, so the newest reading is always the one
 * that survives a reload even when subscribers were not told about it.
 */
const MIN_MOVE_M = 10;
const RENEW_AFTER_MS = 210_000; // 3.5 min, inside the server's 5 min window

/** Metres between two fixes — the same haversine the server uses. */
function metresBetween(a: GeoFix, b: GeoFix): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Would telling subscribers about `next` change any server decision? */
function worthPublishing(next: GeoFix): boolean {
  if (current === null) return true;
  if (next.ts - current.ts >= RENEW_AFTER_MS) return true;
  return metresBetween(current, next) >= MIN_MOVE_M;
}

function accept(pos: GeolocationPosition, forcePublish = false): GeoFix {
  lastError = null;
  // A zero is "not reported", not "perfect" — mock providers and some
  // desktop browsers send it. Treating it as a real reading would print
  // "accurate to about 0 m", which reads as a broken number rather than a
  // missing one.
  const reported = pos.coords.accuracy;
  lastAccuracy = Number.isFinite(reported) && reported > 0 ? reported : null;
  const fix: GeoFix = {
    lat: pos.coords.latitude,
    lng: pos.coords.longitude,
    ts: Date.now(),
  };
  latest = fix;
  persist(fix);
  if (forcePublish || worthPublishing(fix)) publish(fix);
  else setStatus("ready");
  return fix;
}

function refuse(err: GeolocationPositionError): void {
  lastError = err;
  // `current` is deliberately NOT cleared: a fix still inside the server's
  // freshness window stays usable through a transient failure, which is what
  // the cookie's max-age did. Only a failure with nothing to fall back on is
  // a conclusion.
  if (current === null) setStatus("unavailable");
}

// ── public API ──────────────────────────────────────────────────────

export function startGeoTracking(): void {
  if (watching) return;
  watching = true;

  // A fix that survived the reload is still valid to the server, and using
  // it immediately is what stops the /off-premises bounce from looping.
  const saved = restore();
  if (saved !== null) {
    latest = saved;
    publish(saved);
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    setStatus(current === null ? "unavailable" : "ready");
    return;
  }

  const refresh = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => accept(pos),
      (err) => refuse(err),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8_000 },
    );
  };

  refresh();
  setInterval(refresh, REFRESH_MS);

  // A browser that answers neither callback must not leave the app waiting
  // forever — iOS Safari does exactly that when the permission prompt is
  // dismissed by switching apps.
  setTimeout(() => {
    if (status === "pending") setStatus("unavailable");
  }, FIRST_FIX_DEADLINE_MS);
}

export function getGeo(): GeoFix | undefined {
  return current ?? undefined;
}

export function getGeoStatus(): GeoStatus {
  return status;
}

export function getGeoError(): GeolocationPositionError | null {
  return lastError;
}

/** Metres of uncertainty on the last reading, or null if unknown. */
export function getGeoAccuracy(): number | null {
  return lastAccuracy;
}

/** Metres between two points — the same haversine the server uses. */
export function distanceTo(fix: GeoFix, lat: number, lng: number): number {
  return metresBetween(fix, { lat, lng, ts: fix.ts });
}

/** The newest reading, published or not — for the diagnostics on /off-premises. */
export function getLatestReading(): GeoFix | undefined {
  return latest ?? undefined;
}

/**
 * Why the browser will never produce a fix, when that is knowable up front.
 * Worth saying out loud: over plain http — a LAN address during testing, say
 * — geolocation is blocked by the browser and every call fails with a
 * permission error, which otherwise reads as "the user said no".
 */
export function geoBlockedReason(): string | null {
  if (typeof navigator === "undefined") return null;
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return "This page is not on a secure (https) connection, so the browser will not share your location.";
  }
  if (!navigator.geolocation) {
    return "This browser does not support location services.";
  }
  return null;
}

/**
 * Resolve once we know whether a fix is coming — with the fix if there is
 * one. Callers use this instead of reading `getGeo()` straight away, so a
 * call made in the second after page load is not mistaken for a call made
 * from outside the branch.
 */
export function ensureGeoFix(): Promise<GeoFix | undefined> {
  if (status !== "pending") return Promise.resolve(getGeo());
  return new Promise((resolve) => {
    settleWaiters.push(() => resolve(getGeo()));
  });
}

/**
 * An explicit, user-initiated re-read — the "Retry location" button.
 *
 * Unlike the background poll this always publishes: the user asked, so the
 * re-subscription its answer causes is the entire point.
 */
export function refreshGeoNow(): Promise<GeoFix | undefined> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    setStatus(current === null ? "unavailable" : "ready");
    return Promise.resolve(getGeo());
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(accept(pos, true)),
      (err) => {
        refuse(err);
        resolve(getGeo());
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  });
}

export function onGeoChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function onGeoStatusChange(fn: StatusListener): () => void {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}
